/**
 * The office staff, as the settings screen edits them.
 *
 * Two things worth knowing before reading this.
 *
 * **Roles cross the wire as numbers.** The API configures no string enum converter, so a
 * `TenantRole` in a request body is its value rather than its name. The names live here and
 * are mapped once, which is better than either sending numbers from the component or adding
 * a converter that would change every existing payload.
 *
 * **There is no "list the staff" endpoint**, and deliberately so: the question the API
 * answers is "who holds this role", which is what every other caller wants. The list on this
 * screen is those answers merged, because a person can hold both.
 */

import { request, type Schema } from '@taxi/api-client';

/** The two roles the office holds. Riders and drivers are not staff and are not listed here. */
export const staffRoles = ['Dispatcher', 'TenantAdmin'] as const;

export type StaffRole = (typeof staffRoles)[number];

/** The numbers behind them, matching `TenantRole` in the backend. */
const roleValues: Record<StaffRole, number> = {
  Dispatcher: 3,
  TenantAdmin: 4,
};

export interface StaffMember {
  userId: string;
  displayName: string | null;
  roles: StaffRole[];
}

type MembershipRow = Schema<'RoleHolder'>;

export interface InviteStaff {
  email: string;
  roles: StaffRole[];
  displayName: string | null;
}

export type InvitedStaff = Schema<'Invited'>;

function named(values: number[]): StaffRole[] {
  return staffRoles.filter((role) => values.includes(roleValues[role]));
}

export const staffApi = {
  /**
   * Everybody who works in the office, merged from one query per role.
   *
   * Two requests rather than one, because the API's question is "who holds this role" and
   * somebody can hold both — an owner who also answers the phone is the ordinary case at a
   * small firm, and listing them twice would be worse than making two calls.
   */
  list: async (): Promise<StaffMember[]> => {
    const found = new Map<string, StaffMember>();

    for (const role of staffRoles) {
      const rows = await request<MembershipRow[]>(
        `/api/memberships/by-role/${role}`,
      );

      for (const row of rows) {
        found.set(row.userId, {
          userId: row.userId,
          displayName: row.displayName,
          roles: named(row.roles),
        });
      }
    }

    return [...found.values()].sort((left, right) =>
      (left.displayName ?? '').localeCompare(right.displayName ?? ''),
    );
  },

  invite: (body: InviteStaff) =>
    request<InvitedStaff>('/api/memberships/invite', {
      method: 'POST',
      body: {
        email: body.email,
        displayName: body.displayName,
        roles: body.roles.map((role) => roleValues[role]),
      },
    }),

  /**
   * Withdraws somebody's access without deleting them.
   *
   * Suspended rather than revoked: the row survives so whatever they did stays attributable,
   * and the same screen can grant the roles back. The API takes the telephone with it — an
   * extension that outlives the person who used it is a live credential nobody owns.
   */
  suspend: (userId: string) =>
    request<void>(`/api/memberships/${userId}/status`, {
      method: 'POST',
      body: { status: 2 },
    }),

  /**
   * Gives somebody a new authenticator to enrol at their next sign-in.
   *
   * The end of the recovery chain for a phone that went in a river with the recovery codes
   * still in it. Deliberately an act by somebody else — a reset anybody could perform on
   * themselves is not a second factor — and it ends every session they have open.
   */
  resetMfa: (userId: string) =>
    request<void>(`/api/memberships/${userId}/mfa-reset`, { method: 'POST', body: {} }),
};
